import React, { useEffect, useState } from "react";
import { Plus, Pencil, Archive, Trash2, ArrowLeft, Check, AlertTriangle, Globe } from "lucide-react";
import { toast } from "sonner";
import { BottomSheet } from "@/components/BottomSheet";
import { TemplatesPickerSheet } from "@/components/TemplatesPickerSheet";
import {
  createCalendar,
  updateCalendar,
  deleteCalendar,
  CALENDAR_PALETTE,
  CALENDAR_KINDS,
  CALENDAR_VISIBILITY,
} from "@/lib/calendarsApi";
import { useAuth } from "@/context/AuthContext";

// Manage sub-calendars: list → tap-to-edit OR tap "+ New" → form.
// One sheet, two screens, no router pollution.
export function SubCalendarManageSheet({ open, onClose, calendars, onChanged }) {
  const [mode, setMode] = useState("list"); // list | form
  const [editing, setEditing] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);

  useEffect(() => {
    if (open) { setMode("list"); setEditing(null); }
  }, [open]);

  const back = () => { setMode("list"); setEditing(null); };

  return (
    <>
    <BottomSheet
      open={open}
      onClose={onClose}
      title={mode === "list" ? "Calendars" : (editing ? "Edit calendar" : "New calendar")}
      fullHeight
    >
      {mode === "list" ? (
        <ListPane
          calendars={calendars}
          onNew={() => { setEditing(null); setMode("form"); }}
          onEdit={(c) => { setEditing(c); setMode("form"); }}
          onAddFromTemplate={() => setTplOpen(true)}
        />
      ) : (
        <FormPane
          initial={editing}
          onBack={back}
          onSaved={() => { onChanged?.(); back(); }}
        />
      )}
      {mode !== "list" && (
        <button
          type="button"
          onClick={back}
          data-testid="sub-cal-form-back"
          className="absolute top-3 left-3 z-10 inline-flex items-center justify-center w-9 h-9 rounded-full bg-secondary text-foreground tap"
          aria-label="Back"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
      )}
    </BottomSheet>
    <TemplatesPickerSheet
      open={tplOpen}
      onClose={() => setTplOpen(false)}
      onChanged={() => onChanged?.()}
    />
    </>
  );
}

function ListPane({ calendars, onNew, onEdit, onAddFromTemplate }) {
  const live = (calendars || []).filter((c) => !c.is_archived);
  const archived = (calendars || []).filter((c) => c.is_archived);
  return (
    <div className="px-4 pb-6 space-y-3" data-testid="sub-cal-manage-list">
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={onNew}
          data-testid="sub-cal-new-btn"
          className="h-11 rounded-xl bg-foreground text-background font-medium tap inline-flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" /> New
        </button>
        <button
          type="button"
          onClick={onAddFromTemplate}
          data-testid="sub-cal-template-btn"
          className="h-11 rounded-xl border border-border text-foreground font-medium tap inline-flex items-center justify-center gap-2"
        >
          <Globe className="w-4 h-4" /> From template
        </button>
      </div>

      <ul className="divide-y divide-border/60 rounded-xl border border-border/60 overflow-hidden bg-background">
        {live.map((c) => (
          <li key={c.id}>
            <button
              type="button"
              onClick={() => onEdit(c)}
              data-testid={`sub-cal-row-${c.id}`}
              className="w-full flex items-center gap-3 px-4 py-3 tap text-left hover:bg-secondary/40 transition-colors"
            >
              <span className="w-3.5 h-3.5 rounded-full shrink-0" style={{ background: c.color }} />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium truncate">{c.name}</span>
                <span className="block text-[11px] text-muted-foreground">
                  {c.kind === "family" ? "Family default" : c.kind}
                  {" · "}
                  {c.visibility === "family" ? "Everyone" : c.visibility === "parents_only" ? "Parents only" : "Private"}
                </span>
              </span>
              {c.is_default && (
                <span className="text-[10px] text-muted-foreground px-2 py-0.5 rounded-full bg-secondary">
                  default
                </span>
              )}
              <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
          </li>
        ))}
      </ul>

      {archived.length > 0 && (
        <details className="text-xs text-muted-foreground" data-testid="sub-cal-archived">
          <summary className="cursor-pointer py-2">Archived ({archived.length})</summary>
          <ul className="space-y-1 mt-1">
            {archived.map((c) => (
              <li key={c.id} className="flex items-center gap-2 py-1">
                <span className="w-2 h-2 rounded-full" style={{ background: c.color }} />
                {c.name}
                <button
                  type="button"
                  onClick={() => onEdit(c)}
                  className="ml-auto underline"
                >
                  Restore
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function FormPane({ initial, onBack, onSaved }) {
  const { family } = useAuth();
  const isEdit = !!initial;
  const isDefault = !!initial?.is_default;
  const [name, setName] = useState(initial?.name || "");
  const [kind, setKind] = useState(initial?.kind || "personal");
  const [color, setColor] = useState(initial?.color || CALENDAR_PALETTE[0]);
  const [visibility, setVisibility] = useState(initial?.visibility || "family");
  const [archived, setArchived] = useState(!!initial?.is_archived);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (isEdit) {
        await updateCalendar(family.id, initial.id, {
          name: name.trim(),
          kind,
          color,
          visibility,
          is_archived: archived,
        });
        toast.success("Calendar updated");
      } else {
        await createCalendar(family.id, {
          name: name.trim(),
          kind,
          color,
          visibility,
        });
        toast.success("Calendar created");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!isEdit || isDefault) return;
    setSaving(true);
    try {
      await deleteCalendar(family.id, initial.id);
      toast.success("Calendar deleted · events moved to Family");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    } finally {
      setSaving(false);
      setConfirmDel(false);
    }
  };

  return (
    <div className="px-4 pt-12 pb-6 space-y-5" data-testid="sub-cal-form">
      <label className="block">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">Name</span>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="sub-cal-form-name"
          placeholder="e.g. Maya — Hockey"
          className="mt-1 w-full h-11 rounded-xl bg-secondary px-3 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/30"
          autoFocus={!isEdit}
        />
      </label>

      {!isDefault && (
        <div>
          <span className="block text-xs uppercase tracking-wide text-muted-foreground mb-2">Kind</span>
          <div className="grid grid-cols-3 gap-2">
            {CALENDAR_KINDS.map((k) => (
              <button
                key={k.key}
                type="button"
                onClick={() => setKind(k.key)}
                data-testid={`sub-cal-form-kind-${k.key}`}
                className={`h-10 rounded-xl text-xs font-medium tap border transition-colors ${
                  kind === k.key
                    ? "bg-foreground text-background border-foreground"
                    : "bg-background text-foreground border-border"
                }`}
              >
                {k.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <span className="block text-xs uppercase tracking-wide text-muted-foreground mb-2">Colour</span>
        <div className="flex gap-2 flex-wrap" data-testid="sub-cal-form-palette">
          {CALENDAR_PALETTE.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              data-testid={`sub-cal-form-color-${c}`}
              className="w-8 h-8 rounded-full tap relative flex items-center justify-center"
              style={{ background: c }}
              aria-label={`Pick ${c}`}
            >
              {color === c && <Check className="w-4 h-4 text-white drop-shadow" />}
            </button>
          ))}
        </div>
      </div>

      {!isDefault && (
        <div>
          <span className="block text-xs uppercase tracking-wide text-muted-foreground mb-2">Who can see this</span>
          <div className="space-y-1.5">
            {CALENDAR_VISIBILITY.map((v) => (
              <button
                key={v.key}
                type="button"
                onClick={() => setVisibility(v.key)}
                data-testid={`sub-cal-form-vis-${v.key}`}
                className={`w-full text-left p-3 rounded-xl border transition-colors tap ${
                  visibility === v.key
                    ? "bg-secondary border-foreground/40"
                    : "bg-background border-border hover:border-foreground/20"
                }`}
              >
                <div className="text-sm font-medium">{v.label}</div>
                <div className="text-[11px] text-muted-foreground">{v.desc}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {isEdit && !isDefault && (
        <label className="flex items-center justify-between p-3 rounded-xl bg-secondary/40">
          <span className="flex items-center gap-2 text-sm">
            <Archive className="w-4 h-4" />
            Archive (hide but keep events)
          </span>
          <input
            type="checkbox"
            checked={archived}
            onChange={(e) => setArchived(e.target.checked)}
            data-testid="sub-cal-form-archive"
            className="w-5 h-5"
          />
        </label>
      )}

      <button
        type="button"
        onClick={save}
        disabled={saving || !name.trim()}
        data-testid="sub-cal-form-save"
        className="w-full h-11 rounded-xl bg-foreground text-background font-medium tap disabled:opacity-50"
      >
        {saving ? "Saving…" : (isEdit ? "Save changes" : "Create calendar")}
      </button>

      {isEdit && !isDefault && (
        <>
          {!confirmDel ? (
            <button
              type="button"
              onClick={() => setConfirmDel(true)}
              data-testid="sub-cal-form-delete"
              className="w-full h-11 rounded-xl bg-destructive/10 text-destructive font-medium tap inline-flex items-center justify-center gap-2"
            >
              <Trash2 className="w-4 h-4" /> Delete calendar
            </button>
          ) : (
            <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-3 space-y-2" data-testid="sub-cal-form-confirm-delete">
              <div className="flex items-start gap-2 text-xs">
                <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                <span>Delete &ldquo;{initial?.name}&rdquo;? Events on this calendar move to Family. This can&apos;t be undone.</span>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmDel(false)}
                  className="flex-1 h-9 rounded-lg bg-secondary text-sm tap"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onDelete}
                  data-testid="sub-cal-form-confirm-delete-yes"
                  className="flex-1 h-9 rounded-lg bg-destructive text-white text-sm tap"
                >
                  Delete
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {isDefault && (
        <div className="text-[11px] text-muted-foreground text-center">
          This is the Family default. It can&apos;t be deleted &mdash; only renamed or recoloured.
        </div>
      )}
    </div>
  );
}
